import { useState, useEffect } from "react";

import { Container, Grid, Divider } from "@material-ui/core";
import moment from "moment";
import { API_ENV } from "constants/environment";
import { useWebsocketFetch } from "hooks/useWebsocketFetch";
import { useVisionLogin } from "hooks/useVisionLogin";
import useForm from "hooks/form";

import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import LoadingData from "components/Loading/LoadingData";
import HeaderClientes from "components/_refactor/headers/clients";
import InputText from "components/_refactor/text/input";
import FilterCard from "components/_refactor/filter-card";
import StructureFields from "components/_refactor/fields/structure";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import PeriodField from "components/_refactor/fields/period-field";
import TableNotasFiscais from "./components/table-nfs";

const today = new Date().toISOString().slice(0, 10);
const data30DaysAgo = moment(today).subtract(30, "days").format("YYYY-MM-DD");

const statusOptions = [
  {
    name: "Todas",
    Status: "",
  },
  {
    name: "NF Cancelada",
    Status: "NF CANCELADA",
  },
  {
    name: "NF Devolvida Parcialmente",
    Status: "NF DEVOLVIDA PARCIALMENTE",
  },
  {
    name: "NF Devolvida Totalmente",
    Status: "NF DEVOLVIDA TOTALMENTE",
  },
  {
    name: "NF Emitida",
    Status: "NF EMITIDA",
  },
];

export default function ConsultaNotasFiscais() {
  const snackbar = useSnackbar();
  const { userId } = useVisionLogin();

  const { data, handleFetchUrl, handleFetchData, status } = useWebsocketFetch({
    onSuccess: handleGetTableData,
  });

  const { values, handleChange, errors } = useForm({
    nrPedido: "",
    numeroNf: "",
    periodo: null,
    dataInicial: data30DaysAgo,
    dataFinal: today,
  });

  const [searching, setSearching] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const [structureLoading, setStructureLoading] = useState([]);
  const [clearFilters, setClearFilters] = useState(false);
  const [structureFilters, setStructureFilters] = useState({});
  const [hasNoGroup, setHasNoGroup] = useState(false);
  const [storeListInvoices, setStoreListInvoices] = useState(null);
  const [invoiceStatus, setInvoiceStatus] = useState(statusOptions?.[0]);
  const [lastSearchParams, setLastSearchParams] = useState(null);

  const savingData = (tableData) => {
    if (tableData?.length !== 0) {
      setStoreListInvoices(tableData);
    } else {
      setStoreListInvoices([]);
    }
  };

  function handleGetTableData(tableData) {
    setSearching(false);
    savingData(tableData ?? data);
  }

  const handleSearch = async () => {
    const { nrPedido, numeroNf, periodo, dataInicial, dataFinal } = values;

    setStoreListInvoices(null);
    setLastSearchParams(null);
    setSearching(true);
    setCancelLoading(false);
    setErrorMessage(null);

    const initialFormat = moment(dataInicial).format("YYYY-MM-DD");
    const finalFormat = moment(dataFinal).format("YYYY-MM-DD");

    setLastSearchParams({
      idMarca: structureFilters?.idMarca,
      idCanal: structureFilters?.idCanal,
      idRepresentante: structureFilters?.idRepresentante,
      idGrupoEconomico: structureFilters?.idGrupoEconomico,
      idLoja: structureFilters?.idLoja,
      dtIni: initialFormat,
      dtFim: finalFormat,
      numeroPedido: nrPedido,
      numeroNf: numeroNf,
      status: invoiceStatus?.Status || "",
    });
  };

  const handleSendMessage = (message) => {
    handleFetchData(message);
  };

  const handleClearFilters = () => {
    setClearFilters(true);
    handleChange({
      target: {
        name: "periodo",
        value: null,
      },
    });
    handleChange({
      target: {
        name: "dataInicial",
        value: today,
      },
    });
    handleChange({
      target: {
        name: "dataFinal",
        value: today,
      },
    });
    handleChange({
      target: {
        name: "nrPedido",
        value: "",
      },
    });
    handleChange({
      target: {
        name: "numeroNf",
        value: "",
      },
    });
    setInvoiceStatus(statusOptions?.[0]);
    setSearching(false);

    setTimeout(() => {
      setClearFilters(false);
    }, 500);
  };

  const handleStopSearching = () => {
    setCancelLoading(true);
    setSearching(false);
    handleFetchUrl("");
    setStoreListInvoices(null);
  };

  const handleVerifyRequiredInputs = () => {
    if (
      structureFilters?.idGrupoEconomico ||
      values.nrPedido ||
      values.numeroNf
    ) {
      return handleSearch();
    } else {
      if (
        !structureFilters?.idGrupoEconomico &&
        !values.nrPedido &&
        !values.numeroNf
      ) {
        setHasNoGroup(true);
      }
    }
  };

  useEffect(() => {
    if (errorMessage && !cancelLoading) {
      snackbar.showMessage(errorMessage, { severity: "error" });
    }
  }, [cancelLoading, errorMessage]);

  useEffect(() => {
    if (
      structureFilters.idGrupoEconomico !== "" ||
      values.nrPedido ||
      values.numeroNf
    ) {
      setHasNoGroup(false);
    }
  }, [structureFilters, values]);

  useEffect(() => {
    if (
      status === "empty" &&
      !cancelLoading &&
      !searching &&
      storeListInvoices?.length === 0
    ) {
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    } else if (
      status === "empty" &&
      !cancelLoading &&
      searching &&
      storeListInvoices === null
    ) {
      setSearching(false);
      snackbar.showMessage("Sua busca não retornou nenhum resultado.", {
        severity: "success",
      });
    }
  }, [status, cancelLoading, searching, storeListInvoices]);

  useEffect(() => {
    if (lastSearchParams && userId) {
      handleSendMessage({
        action: "list-invoices-store",
        params: {
          querystring: lastSearchParams,
          header: {
            "K-IdUser": userId,
          },
        },
      });
    }
  }, [lastSearchParams, userId]);

  useEffect(() => {
    if (
      cancelLoading &&
      storeListInvoices === null &&
      lastSearchParams === null
    ) {
      handleFetchUrl(
        `wss://3zvsu3yj2b.execute-api.us-east-1.amazonaws.com/${API_ENV}`
      );
    }
  }, [cancelLoading, storeListInvoices, lastSearchParams]);

  useEffect(() => {
    handleFetchUrl(
      `wss://3zvsu3yj2b.execute-api.us-east-1.amazonaws.com/${API_ENV}`
    );
  }, []);

  return (
    <>
      <HeaderClientes
        section="Consulta de Notas Fiscais"
        subsection="Notas Fiscais"
        tooltipMessage="Para garantir melhor performance e usabilidade, a extração deste relatório será realizada em segundo plano (background). Isso permite que você continue utilizando a ferramenta do Portal Karsten normalmente enquanto o relatório é processado. Assim que a geração for concluída, o arquivo ficará disponível para download na central de relatório de Notas Fiscais."
      />
      <Container style={{ marginTop: "-4.5rem" }} maxWidth={false}>
        <FilterCard
          handleSearch={handleVerifyRequiredInputs}
          disabled={
            searching ||
            Object.entries(errors).length > 0 ||
            structureLoading.length > 0
          }
          searching={searching}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            defaultBrand={{
              IdMarca: "TODAS",
              Nome: "Todas",
            }}
            clearFilters={clearFilters}
            onStructureFieldsChange={setStructureFilters}
            disableAll={searching}
            onStructureFieldsLoad={setStructureLoading}
            requiredFields={["economicGroup"]}
            fieldsOrder={["economicGroup", "store"]}
            hasNoGroup={hasNoGroup}
            handleSearch={handleVerifyRequiredInputs}
          />

          <Grid item xs={12}>
            <Divider style={{ margin: "0.5rem 0" }} />
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <InputText
              label="Número do Pedido"
              value={values.nrPedido}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "nrPedido",
                    value: e.target.value.slice(0, 50),
                  },
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleVerifyRequiredInputs();
                }
              }}
              disabled={searching}
              error={!!errors.nrPedido}
              errorMessage={errors.nrPedido}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <InputText
              label="Número da NF"
              value={values.numeroNf}
              onChange={(e) => {
                handleChange({
                  target: {
                    name: "numeroNf",
                    value: e.target.value.slice(0, 50),
                  },
                });
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleVerifyRequiredInputs();
                }
              }}
              disabled={searching}
              error={!!errors.numeroNf}
              errorMessage={errors.numeroNf}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <AutoCompleteField
              options={statusOptions}
              label="Status"
              value={invoiceStatus}
              onChange={(value) => {
                setInvoiceStatus(value);
              }}
              disabled={searching}
              loading={searching}
              optionLabelKey="name"
              optionIdKey="Status"
            />
          </Grid>
          <Grid
            item
            xs={12}
            md={values.periodo?.value === "Outros" ? "12" : 6}
            lg={values.periodo?.value === "Outros" ? "12" : 3}
          >
            <PeriodField
              label="Período"
              value={values.periodo}
              onChange={(option) =>
                handleChange({
                  target: {
                    name: "periodo",
                    value: option,
                  },
                })
              }
              initialDate={values.dataInicial}
              finalDate={values.dataFinal}
              setInitialDate={(value) =>
                handleChange({
                  target: {
                    name: "dataInicial",
                    value,
                  },
                })
              }
              setFinalDate={(value) =>
                handleChange({
                  target: {
                    name: "dataFinal",
                    value,
                  },
                })
              }
              disabled={searching || structureLoading.length > 0}
            />
          </Grid>
        </FilterCard>

        {storeListInvoices && !cancelLoading && !searching ? (
          <TableNotasFiscais dataTable={storeListInvoices} />
        ) : (
          <LoadingData
            searching={searching}
            onClick={() => handleStopSearching()}
            label="Buscando notas fiscais..."
          />
        )}
      </Container>
    </>
  );
}
